import { useResizeObserver } from "@react-aria/utils";
import { createContext, useContext, useRef, type RefObject } from "react";
import {
  useOverlayPosition as useAriaOverlayPosition,
  type AriaPositionProps,
} from "react-aria";

import { useUiOptions } from "./ui-options-context";

export const OverlayContext = createContext<
  | {
      container: RefObject<HTMLDivElement> | null;
    }
  | undefined
>(undefined);

export const OverlayContextProvider = OverlayContext.Provider;

/**
 * workaround for the issue with the overlay position when the layout change size.
 * In useOverlayPosition, scroll event handler triggers on the layout change size in useCloseOnScroll Hook.
 * Using position updating on close event to prevent popover close on layout change size.
 * https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/overlays/src/useOverlayPosition.ts#L235
 * https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/overlays/src/useCloseOnScroll.ts#L51
 */
export const useOverlayPosition = ({
  overlayRef,
  targetRef,
  ...restProps
}: AriaPositionProps) => {
  const options = useUiOptions();
  const overlayContext = useContext(OverlayContext);

  const isHookEnabled = options.experimental?.renderPopoversInsideAppContainer;
  const nullRef = useRef(null);

  /**
   * workaround for the issue with the popover position when the trigger change size.
   * usePopover doesn't update the position of the popover when the trigger changes size.
   * using useOverlayPosition to update the position of the popover when the trigger changes size.
   * https://github.com/adobe/react-spectrum/issues/4040
   */
  const { overlayProps, updatePosition } = useAriaOverlayPosition({
    ...restProps,
    overlayRef: isHookEnabled ? overlayRef : nullRef,
    targetRef: isHookEnabled ? targetRef : nullRef,
    onClose: () => updatePosition(),
  });

  useResizeObserver({
    ref: isHookEnabled ? overlayContext?.container ?? nullRef : nullRef,
    onResize: () => {
      updatePosition();
    },
  });

  if (!isHookEnabled) {
    return {};
  }

  if (!overlayContext)
    throw new Error("Layout must have an access to OverlayContext");

  return overlayProps;
};
